/** Núcleo puro de breeding do IdleMon. Sem DOM, estado global ou I/O. */

import {
  IV_STAT_MAX,
  IV_TOTAL_MAX,
  ivPercent,
  ivScore,
  specimenTierForMult,
} from '../capture-balance.mjs';

const IV_KEYS = Object.freeze(['HP', 'ATK', 'DEF', 'VEL', 'ESP', 'RES']);
const IV_ALIASES = Object.freeze({ HP: 'hp', ATK: 'atk', DEF: 'def', VEL: 'spe', ESP: 'spa', RES: 'spd' });

/**
 * Modos de breeding:
 * - `pheromones`: consome Feromônios e rola a tabela cheia (Δ +0.15 a +0.30).
 * - `free`: grátis (sem Feromônios), Δ ~20× menor e ~20% de desconto no custo.
 */
export const BREEDING_MODES = Object.freeze(['pheromones', 'free']);
const DELTA_TABLE = Object.freeze([
  Object.freeze({ value: 0.15, weight: 0.50 }),
  Object.freeze({ value: 0.20, weight: 0.30 }),
  Object.freeze({ value: 0.25, weight: 0.15 }),
  Object.freeze({ value: 0.30, weight: 0.05 }),
]);
const FREE_DELTA_TABLE = Object.freeze([
  Object.freeze({ value: 0.005, weight: 0.50 }),
  Object.freeze({ value: 0.01, weight: 0.35 }),
  Object.freeze({ value: 0.02, weight: 0.12 }),
  Object.freeze({ value: 0.04, weight: 0.03 }),
]);
const FREE_MODE_DISCOUNT = 0.20;
const DOUBLE_STONES_INHERIT_BONUS = 0.15;
const DOUBLE_STONES_IV_ROLL = 0.05;
const INCUBATION_BY_TIER = Object.freeze({
  Comum: 600,
  Incomum: 900,
  Raro: 1200,
  Épico: 1800,
  Lendário: 2500,
  Mítico: 3200,
  Celestial: 4000,
  Ancião: 5200,
  Secreto: 7000,
});

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
const randomUnit = (random) => clamp(Number(random()) || 0, 0, 0.999999);
const speciesSlug = (parent) => String(parent?.species || parent?.slug || parent?.name || '')
  .trim().toLowerCase().replace(/_/g, '-');

export function normalizeBreedingMode(mode) {
  const key = String(mode || '').trim().toLowerCase();
  return key === 'free' || key === 'gratis' ? 'free' : 'pheromones';
}

function normalizedIvs(parent) {
  return Object.fromEntries(IV_KEYS.map((key) => [
    key,
    clamp(Math.round(Number(parent?.ivs?.[key] ?? parent?.ivs?.[IV_ALIASES[key]]) || 0), 0, IV_STAT_MAX),
  ]));
}

function parentMult(parent) {
  return clamp(Number(parent?.mult) || 1, 1, 4.5);
}

function bestParent(parentA, parentB) {
  const multA = parentMult(parentA), multB = parentMult(parentB);
  if (multA !== multB) return multA > multB ? parentA : parentB;
  return ivScore(parentA?.ivs) >= ivScore(parentB?.ivs) ? parentA : parentB;
}

/**
 * Mesma espécie é requisito fechado. Quanto mais próxima a qualidade dos pais,
 * maior a compatibilidade (1.0 = mesma quality; piso 0.5 com gap ≥ 0.625).
 */
export function breedingCompatibility(parentA, parentB) {
  const speciesA = speciesSlug(parentA), speciesB = speciesSlug(parentB);
  if (!speciesA || speciesA !== speciesB) return 0;
  const qualityGap = Math.abs(parentMult(parentA) - parentMult(parentB));
  return Math.round(clamp(1 - qualityGap / 1.25, 0.5, 1) * 1000) / 1000;
}

/** Chance por stat de copiar o IV do melhor pai (exibida como "Herança de IV"). */
export function ivInheritanceChance(parentA, parentB, { doubleStones = false } = {}) {
  const compatibility = breedingCompatibility(parentA, parentB);
  if (!compatibility) return 0;
  return clamp(0.55 + compatibility * 0.45 + (doubleStones ? DOUBLE_STONES_INHERIT_BONUS : 0), 0, 1);
}

function singleDeltaRoll(random, table) {
  let cursor = randomUnit(random);
  for (const row of table) {
    cursor -= row.weight;
    if (cursor < 0) return row.value;
  }
  return table.at(-1).value;
}

/**
 * Rola o Δ de quality do filho. O modo define a tabela: `pheromones` usa a
 * cheia (+0.15..+0.30 @ 50/30/15/5); `free` a reduzida (~20× menor @ 50/35/12/3).
 * Legado: `{ pheromones: n > 0 }` equivale ao modo `pheromones` com rolagens
 * extras (máx. 3), mantendo o melhor resultado.
 */
export function rollBreedingDelta({ mode, pheromones } = {}, random = Math.random) {
  if (mode != null) {
    const table = normalizeBreedingMode(mode) === 'free' ? FREE_DELTA_TABLE : DELTA_TABLE;
    return singleDeltaRoll(random, table);
  }
  const bonusRolls = Math.min(3, Math.max(0, Math.floor(Number(pheromones) || 0)));
  const rolls = 1 + bonusRolls;
  let best = DELTA_TABLE[0].value;
  for (let index = 0; index < rolls; index++) best = Math.max(best, singleDeltaRoll(random, DELTA_TABLE));
  return best;
}

/** Tabela de Δ exibida na UI (pílulas de chance) para o modo escolhido. */
export function deltaTableForMode(mode) {
  return normalizeBreedingMode(mode) === 'free' ? FREE_DELTA_TABLE : DELTA_TABLE;
}

/** Alcance de quality projetado do filho: melhor pai + [menor Δ, maior Δ] (teto 4.5). */
export function projectedQualityRange(parentA, parentB, { mode } = {}) {
  const donor = bestParent(parentA, parentB);
  const base = parentMult(donor);
  const table = deltaTableForMode(mode);
  return {
    base,
    min: Math.round(clamp(base + table[0].value, 1, 4.5) * 1000) / 1000,
    max: Math.round(clamp(base + table.at(-1).value, 1, 4.5) * 1000) / 1000,
  };
}

/**
 * Custo em Feromônios do modo `pheromones`: escala com a quality do melhor pai
 * (1 no Comum ~1.0; ~9 no Épico 1.57; 56 no teto 4.5).
 */
export function pheromoneCostFor(childOrMult) {
  const mult = clamp(Number(typeof childOrMult === 'object' ? childOrMult?.mult : childOrMult) || 1, 1, 4.5);
  return Math.max(1, Math.round((mult - 1) * 16));
}

/** Gera o filho nível 1; consumo dos pais e criação do ovo ficam na camada chamadora. */
export function breedChild(parentA, parentB, {
  mode,
  pheromones,
  doubleStones = false,
  random = Math.random,
} = {}) {
  const compatibility = breedingCompatibility(parentA, parentB);
  if (!compatibility) throw new RangeError('breeding_incompatible_parents');

  const donor = bestParent(parentA, parentB);
  const other = donor === parentA ? parentB : parentA;
  const donorIvs = normalizedIvs(donor), otherIvs = normalizedIvs(other);
  const inheritanceChance = clamp(
    0.55 + compatibility * 0.45 + (doubleStones ? DOUBLE_STONES_INHERIT_BONUS : 0), 0, 1,
  );
  const ivs = {};

  for (const key of IV_KEYS) {
    if (randomUnit(random) < inheritanceChance) {
      ivs[key] = donorIvs[key];
    } else {
      const source = randomUnit(random) < 0.7 ? otherIvs[key] : donorIvs[key];
      const mutation = Math.floor(randomUnit(random) * 5) - 2;
      ivs[key] = clamp(source + mutation, 0, IV_STAT_MAX);
    }
    if (doubleStones && randomUnit(random) < DOUBLE_STONES_IV_ROLL) {
      ivs[key] = clamp(ivs[key] + 1, 0, IV_STAT_MAX);
    }
  }

  const delta = rollBreedingDelta({ mode, pheromones }, random);
  const mult = Math.round(clamp(parentMult(donor) + delta, 1, 4.5) * 100) / 100;
  const tier = specimenTierForMult(mult);
  return {
    species: speciesSlug(donor),
    ivs,
    ivScore: ivScore(ivs),
    ivTotal: ivPercent(ivs),
    mult,
    rarityName: tier.name,
  };
}

/**
 * `child.type` ou `child.types` define as stones elementais. Se a camada de UI
 * só tiver o retorno mínimo de breedChild, deve anexar os tipos do Pokédex.
 * `doubleStones` dobra a quantidade de stones; o modo `free` dá 20% de desconto.
 */
export function breedingCost(child, { mode, doubleStones = false, discount } = {}) {
  const mult = clamp(Number(child?.mult) || 1, 1, 4.5);
  const types = [...new Set([...(Array.isArray(child?.types) ? child.types : []), child?.type].filter(Boolean))];
  const isFree = mode != null ? normalizeBreedingMode(mode) === 'free' : discount === true;
  const discountRate = isFree ? FREE_MODE_DISCOUNT : clamp(Number(discount) || 0, 0, FREE_MODE_DISCOUNT);
  const coins = Math.max(1000, Math.round((12000 + mult * mult * 8500) * (1 - discountRate) / 100) * 100);
  const stoneQty = Math.max(1, Math.ceil((3 + mult * 2.5) * (1 - discountRate))) * (doubleStones ? 2 : 1);
  const stones = Object.fromEntries((types.length ? types : ['normal']).map((type) => [type, stoneQty]));
  const pheromones = isFree ? 0 : pheromoneCostFor(mult);
  return { coins, stones, pheromones, mode: isFree ? 'free' : 'pheromones', doubleStones: !!doubleStones };
}

export function incubationDefeatsFor(child) {
  const tier = child?.rarityName || specimenTierForMult(child?.mult).name;
  return Math.round(INCUBATION_BY_TIER[tier] || INCUBATION_BY_TIER.Comum);
}

export const BREEDING_DELTA_TABLE = DELTA_TABLE;
export const BREEDING_FREE_DELTA_TABLE = FREE_DELTA_TABLE;
export const BREEDING_FREE_DISCOUNT = FREE_MODE_DISCOUNT;
export const BREEDING_DOUBLE_STONES_INHERIT_BONUS = DOUBLE_STONES_INHERIT_BONUS;
export const BREEDING_DOUBLE_STONES_IV_ROLL = DOUBLE_STONES_IV_ROLL;
export const BREEDING_IV_TOTAL_MAX = IV_TOTAL_MAX;
